import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Box, Button, Card, CardContent, CardHeader, Flex, Heading, LoadingOrbit, Text, Select, Warning, CheckCircle, useToast, TagBadge, type SelectOption, badgeVariantToTagColor } from '@chatsdet/ui';
import { API_URL } from '../../../config.js';
import { parseApiJson, toApiError } from '../../../lib/api-error';
import { BRAND } from '../../../brand';

const FIVE_MIN = 5 * 60 * 1000;

type JiraProject = { id: string; key: string; name: string };
type JiraIssueType = { id: string; name: string };

type JiraSettings = {
  defaultProjectKey: string | null;
  defaultIssueType: string | null;
  defaultLabels: string[] | null;
};

type JiraWebhookStatus = {
  state: 'active' | 'degraded';
  capabilityEnabled: boolean;
  reason?:
    | 'not_connected'
    | 'missing_default_project'
    | 'missing_webhook'
    | 'registration_failed'
    | 'api_origin_unavailable';
  projectKey: string | null;
  webhookId: string | null;
  jqlFilter: string | null;
  expiresAt: string | null;
};

type Props = {
  jiraQuery: UseQueryResult<{ ok: boolean; details?: unknown }>;
  disconnectJiraMutation: UseMutationResult<unknown, Error, void>;
};

export function JiraSettingsSection({ jiraQuery, disconnectJiraMutation }: Props) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const name = BRAND.name;

  const connected = !!jiraQuery.data?.ok;

  const settingsQuery = useQuery({
    queryKey: ['jira-settings'],
    queryFn: async (): Promise<{ ok: boolean; data?: JiraSettings }> => {
      const res = await fetch(`${API_URL}/integrations/jira/settings`, {
        credentials: 'include',
      });
      return parseApiJson<{ ok: boolean; data?: JiraSettings }>(
        res,
        'Failed to fetch Jira settings'
      );
    },
    enabled: connected,
  });

  const projectsQuery = useQuery({
    queryKey: ['jira-projects'],
    queryFn: async (): Promise<{ ok: boolean; data?: JiraProject[] }> => {
      const res = await fetch(`${API_URL}/integrations/jira/projects`, {
        credentials: 'include',
      });
      return parseApiJson<{ ok: boolean; data?: JiraProject[] }>(
        res,
        'Failed to fetch Jira projects'
      );
    },
    enabled: connected,
    staleTime: FIVE_MIN,
  });

  const [selectedProjectKey, setSelectedProjectKey] = React.useState<string | null>(null);
  const [selectedIssueType, setSelectedIssueType] = React.useState<string | null>(null);
  const [dirty, setDirty] = React.useState(false);

  React.useEffect(() => {
    if (settingsQuery.data?.data && !dirty) {
      setSelectedProjectKey(settingsQuery.data.data.defaultProjectKey);
      setSelectedIssueType(settingsQuery.data.data.defaultIssueType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsQuery.data]);

  const issueTypesQuery = useQuery({
    queryKey: ['jira-issue-types', selectedProjectKey],
    queryFn: async (): Promise<{ ok: boolean; data?: JiraIssueType[] }> => {
      const res = await fetch(
        `${API_URL}/integrations/jira/issue-types?projectKey=${encodeURIComponent(
          selectedProjectKey!
        )}`,
        { credentials: 'include' }
      );
      return parseApiJson<{ ok: boolean; data?: JiraIssueType[] }>(
        res,
        'Failed to fetch Jira issue types'
      );
    },
    enabled: !!selectedProjectKey,
    staleTime: FIVE_MIN,
  });

  const webhookQuery = useQuery({
    queryKey: ['jira-webhook'],
    queryFn: async (): Promise<{ ok: boolean; data: JiraWebhookStatus }> => {
      const res = await fetch(`${API_URL}/integrations/jira/webhook`, {
        credentials: 'include',
      });
      return parseApiJson<{ ok: boolean; data: JiraWebhookStatus }>(
        res,
        'Failed to fetch Jira webhook status'
      );
    },
    enabled: connected,
  });

  const saveSettingsMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${API_URL}/integrations/jira/settings`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          defaultProjectKey: selectedProjectKey,
          defaultIssueType: selectedIssueType,
        }),
      });
      if (!res.ok) throw await toApiError(res);
      return res.json() as Promise<{ ok: boolean; data?: { webhook?: JiraWebhookStatus } }>;
    },
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ['jira-settings'] });
      qc.invalidateQueries({ queryKey: ['jira-webhook'] });
      setDirty(false);
      if (result.data?.webhook?.state === 'degraded') {
        toast.warning('Jira settings saved, but status sync still needs attention');
        return;
      }
      toast.success('Jira settings saved');
    },
  });

  const registerWebhookMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${API_URL}/integrations/jira/webhook/register`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          projectKey: selectedProjectKey ?? settingsQuery.data?.data?.defaultProjectKey ?? null,
        }),
      });
      if (!res.ok) throw await toApiError(res);
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['jira-webhook'] });
      toast.success('Jira status sync repaired');
    },
    onError: (err: Error) => toast.error(err.message || 'Failed to register webhook'),
  });

  const removeWebhookMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${API_URL}/integrations/jira/webhook`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) throw await toApiError(res);
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['jira-webhook'] });
      toast.success('Jira webhook removed');
    },
  });

  const projectOptions = React.useMemo<SelectOption[]>(
    () =>
      (projectsQuery.data?.data ?? []).map((p) => ({
        id: p.key,
        label: `${p.name} (${p.key})`,
      })),
    [projectsQuery.data]
  );

  const issueTypeOptions = React.useMemo<SelectOption[]>(
    () =>
      (issueTypesQuery.data?.data ?? []).map((t) => ({
        id: t.name,
        label: t.name,
      })),
    [issueTypesQuery.data]
  );

  const webhookStatus = webhookQuery.data?.data;
  const webhookConfigured = webhookStatus?.state === 'active';
  const webhookNeedsRepair =
    webhookStatus?.state === 'degraded' && webhookStatus.reason !== 'missing_default_project';

  // Keep this hook above the early returns below — gating it behind `connected`
  // makes the hook count change across renders and throws React error #310
  // ("Rendered more hooks than during the previous render") when Jira connects.
  const webhookStatusMessage = React.useMemo(() => {
    if (!webhookStatus) return 'Checking Jira sync status...';
    if (!webhookStatus.capabilityEnabled) {
      return 'Status sync is turned off for this organization.';
    }
    if (webhookStatus.state === 'active') {
      return 'Jira status changes sync automatically.';
    }

    switch (webhookStatus.reason) {
      case 'missing_default_project':
        return 'Choose a default Jira project and save settings to enable status sync.';
      case 'missing_webhook':
        return 'Jira is connected, but the sync webhook is missing. Save settings or repair sync.';
      case 'registration_failed':
        return `${name} could not register the Jira webhook. Repair sync after confirming access.`;
      case 'api_origin_unavailable':
        return `${name} could not determine the public API URL needed for Jira webhooks.`;
      default:
        return 'Jira sync needs attention.';
    }
  }, [webhookStatus, name]);

  if (jiraQuery.isLoading) {
    return (
      <Box className="space-y-6">
        <Flex align="center" justify="center" className="py-8">
          <LoadingOrbit size="md" />
        </Flex>
      </Box>
    );
  }

  if (!connected) {
    return (
      <Box className="space-y-6">
        <Card padding="lg">
          <CardHeader>
            <Flex align="start" gap="md">
              <div className="p-2 bg-warning/10 rounded-lg">
                <Warning size={24} className="text-warning" />
              </div>
              <div className="flex-1">
                <Flex align="center" gap="sm">
                  <Heading level={3}>Jira Integration</Heading>
                  <TagBadge name="Not Connected" color="yellow" hideDot />
                </Flex>
                <Text size="sm" className="text-muted-foreground mt-1">
                  Connect your Jira account to create tickets from issues and sync status
                </Text>
              </div>
            </Flex>
          </CardHeader>
          <CardContent>
            <Text size="sm" className="text-muted-foreground mb-4">
              Securely connect via OAuth to create Jira tickets from {name} issues and
              auto-close issues when tickets are resolved
            </Text>
            <Button
              variant="primary"
              onPress={() => {
                window.location.href = `${API_URL}/integrations/jira/oauth/start`;
              }}
            >
              Connect with OAuth
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box className="space-y-6">
      <Card padding="lg">
        <CardHeader>
          <Flex align="start" gap="md">
            <div className="p-2 bg-success/10 rounded-lg">
              <CheckCircle size={24} className="text-success" />
            </div>
            <div className="flex-1">
              <Flex align="center" gap="sm">
                <Heading level={3}>Jira Integration</Heading>
                <TagBadge name="Connected" color="blue" hideDot />
                {webhookConfigured && <TagBadge name="Webhooks Active" color="blue" hideDot />}
              </Flex>
              <Text size="sm" className="text-muted-foreground mt-1">
                Issues can be linked to Jira tickets. Status changes sync via webhook.
              </Text>
            </div>
            <Button
              variant="destructive"
              size="sm"
              onPress={() => {
                if (window.confirm('Are you sure you want to disconnect Jira?')) {
                  disconnectJiraMutation.mutate();
                }
              }}
              isDisabled={disconnectJiraMutation.isPending}
            >
              {disconnectJiraMutation.isPending ? 'Disconnecting...' : 'Disconnect'}
            </Button>
          </Flex>
        </CardHeader>
      </Card>

      <Card padding="lg">
        <CardHeader>
          <Heading level={3}>Default Settings</Heading>
          <Text size="sm" className="text-muted-foreground mt-1">
            Configure defaults for Jira tickets created from {name} issues
          </Text>
        </CardHeader>
        <CardContent>
          <Flex direction="column" gap="lg">
            <div>
              <Text size="sm" className="font-medium mb-1.5">
                Default Project
              </Text>
              {projectsQuery.isLoading ? (
                <Text size="sm" className="text-muted-foreground">
                  Loading projects...
                </Text>
              ) : (
                <Select
                  aria-label="Default Jira project"
                  selectedKey={selectedProjectKey}
                  items={projectOptions}
                  placeholder="Select a project..."
                  onSelectionChange={(key) => {
                    setSelectedProjectKey(key as string);
                    setSelectedIssueType(null);
                    setDirty(true);
                  }}
                />
              )}
            </div>

            {selectedProjectKey && (
              <div>
                <Text size="sm" className="font-medium mb-1.5">
                  Default Issue Type
                </Text>
                {issueTypesQuery.isLoading ? (
                  <Text size="sm" className="text-muted-foreground">
                    Loading issue types...
                  </Text>
                ) : (
                  <Select
                    aria-label="Default Jira issue type"
                    selectedKey={selectedIssueType}
                    items={issueTypeOptions}
                    placeholder="Select an issue type..."
                    onSelectionChange={(key) => {
                      setSelectedIssueType(key as string);
                      setDirty(true);
                    }}
                  />
                )}
              </div>
            )}

            <Flex gap="sm">
              <Button
                variant="primary"
                size="sm"
                onPress={() => saveSettingsMutation.mutate()}
                isDisabled={!dirty || saveSettingsMutation.isPending}
              >
                {saveSettingsMutation.isPending ? 'Saving...' : 'Save Settings'}
              </Button>
            </Flex>
          </Flex>
        </CardContent>
      </Card>

      <Card padding="lg">
        <CardHeader>
          <Heading level={3}>Status Sync (Webhook)</Heading>
          <Text size="sm" className="text-muted-foreground mt-1">
            When a Jira ticket moves to Done, the linked {name} issue is closed automatically.
          </Text>
        </CardHeader>
        <CardContent>
          <Flex direction="column" gap="sm">
            <Flex align="center" gap="sm">
              <TagBadge name={webhookConfigured ? 'Sync Active' : 'Needs Attention'} color={badgeVariantToTagColor(webhookConfigured ? 'success' : 'warning')} hideDot />
              {!webhookStatus?.capabilityEnabled && <TagBadge name="Disabled" color="blue" hideDot />}
            </Flex>
            <Text size="sm" className="text-muted-foreground">
              {webhookStatusMessage}
            </Text>
            {webhookConfigured && webhookStatus?.jqlFilter && (
              <Text size="sm">
                Active webhook filter: <code>{webhookStatus.jqlFilter}</code>
              </Text>
            )}
            {webhookConfigured && webhookStatus?.expiresAt && (
              <Text size="sm" className="text-muted-foreground">
                Expires {new Date(webhookStatus.expiresAt).toLocaleDateString()} (auto-renewed in
                the background).
              </Text>
            )}
            <Flex gap="sm">
              {webhookNeedsRepair && (
                <Button
                  variant="outline"
                  size="sm"
                  onPress={() => registerWebhookMutation.mutate()}
                  isDisabled={
                    registerWebhookMutation.isPending ||
                    !(selectedProjectKey ?? settingsQuery.data?.data?.defaultProjectKey)
                  }
                >
                  {registerWebhookMutation.isPending ? 'Repairing...' : 'Repair Sync'}
                </Button>
              )}
              {webhookConfigured && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onPress={() => registerWebhookMutation.mutate()}
                    isDisabled={registerWebhookMutation.isPending}
                  >
                    {registerWebhookMutation.isPending ? 'Re-registering...' : 'Re-register'}
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onPress={() => removeWebhookMutation.mutate()}
                    isDisabled={removeWebhookMutation.isPending}
                  >
                    {removeWebhookMutation.isPending ? 'Removing...' : 'Remove'}
                  </Button>
                </>
              )}
            </Flex>
          </Flex>
        </CardContent>
      </Card>

      <Text size="sm" className="text-muted-foreground">
        Tag-triggered ticket tests are configured on the{' '}
        <Link to="/tickets" className="underline">
          Tickets page
        </Link>
        .
      </Text>
    </Box>
  );
}
